import { Component } from '@theme/component';
import { ensureGsap } from '@akr/animate-gsap';

/**
 * Preorder product card: swaps the visible gallery image on thumbnail click,
 * and keeps the price sticker in sync with the selected variant radio.
 * Add to cart is handled by Horizon's own <product-form-component>/
 * <add-to-cart-component> (see akr-preorder-product.liquid) — this class only
 * owns the gallery/variant-picker/description UI, not the cart flow itself.
 *
 * @extends {Component<{images: HTMLImageElement[], thumbs: HTMLButtonElement[], gallery: HTMLElement}>}
 */
export class PreorderProduct extends Component {
  /** Index into `this.refs.images` of the currently visible layer. */
  #activeImageIndex = 0;

  /** Horizontal distance (px) a touch has to travel before it counts as a swipe. */
  static #swipeThreshold = 40;

  /** @type {number} */
  #swipeStartX = 0;
  /** @type {number} */
  #swipeStartY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#updateSeeMoreVisibility();
    document.fonts?.ready.then(() => this.#updateSeeMoreVisibility());
    window.addEventListener('resize', this.#updateSeeMoreVisibility);
    // `on:pointerdown` (the declarative system) has no matching
    // pointerup/pointermove counterpart to pair it with, so the swipe
    // gesture is wired imperatively instead — mobile only (mouse drags
    // shouldn't hijack image selection on desktop, where clicking a
    // thumbnail already does that job).
    this.refs.gallery?.addEventListener('pointerdown', this.#onSwipeStart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#updateSeeMoreVisibility);
    this.refs.gallery?.removeEventListener('pointerdown', this.#onSwipeStart);
    this.#removeSwipeEndListeners();
  }

  /**
   * The editor morphs this section's DOM back to the liquid-rendered
   * default (product/variant setting changed, etc.) — refs.images are then
   * two fresh <img> elements matching that default markup (first one
   * `.is-active`, second hidden), so our tracked index must reset to match
   * or the next swap would target the wrong layer.
   */
  updatedCallback() {
    this.#activeImageIndex = 0;
  }

  selectImage(event) {
    const { src, srcset } = event.target.dataset;
    this.#setActiveThumb((thumb) => thumb === event.target);
    this.#swapImage(src, srcset);
  }

  selectPreviousImage() {
    this.#goToAdjacentThumb(-1);
  }

  selectNextImage() {
    this.#goToAdjacentThumb(1);
  }

  /**
   * Animates the clamped description open/closed instead of the instant
   * line-clamp snap. FLIP-style: measure the height before toggling the
   * class (old state), toggle it (CSS clamp/unclamp applies synchronously,
   * so measuring again immediately gives the new state's natural height),
   * then let GSAP tween between the two — clearProps after so a later
   * resize/font-load recheck isn't fighting a leftover inline height.
   */
  toggleDescription() {
    const description = this.querySelector('.AkrPreorderProduct-description');
    if (!description) return;

    const expanding = !this.classList.contains('is-description-expanded');
    // Not a `ref` — it now sits inside <product-form-component>, which
    // claims it as its own nearest-ancestor-component ref instead of ours.
    this.querySelector('.AkrPreorderProduct-seeMore')?.setAttribute(
      'aria-expanded',
      String(expanding),
    );

    ensureGsap().then(() => {
      gsap.killTweensOf(description);

      const startHeight = description.clientHeight;
      this.classList.toggle('is-description-expanded', expanding);
      const endHeight = description.clientHeight;

      gsap.fromTo(
        description,
        { height: startHeight },
        {
          height: endHeight,
          duration: 0.3,
          ease: 'power2.inOut',
          clearProps: 'height',
        },
      );
    });
  }

  /**
   * The "See more" button is only useful when the clamped description
   * actually overflows — shows/hides it via a class rather than always
   * rendering it, re-checked on font load (Special Elite swapping in can
   * reflow line count) and on resize (the responsive rem scale changes how
   * much text fits per line).
   */
  #updateSeeMoreVisibility = () => {
    const description = this.querySelector('.AkrPreorderProduct-description');
    if (!description) return;

    this.classList.toggle(
      'has-description-overflow',
      description.scrollHeight > description.clientHeight + 1,
    );
  };

  updatePrice(event) {
    const { price, available } = event.target.dataset;
    if (!price) return;

    // Two copies in the markup (landscape badge in the gallery, portrait
    // one in the card body — see akr-preorder-product.liquid) — not `ref`s:
    // the portrait one sits inside <product-form-component>, which would
    // claim it as its own nearest-ancestor-component ref instead of ours.
    for (const priceEl of this.querySelectorAll('.AkrPreorderProduct-price')) {
      priceEl.textContent = price;
    }

    this.#updateAddToCartAvailability(available !== 'false');
  }

  /**
   * Disables the checkout button and swaps its label to the sold-out text
   * when the just-selected variant isn't available — mirrors what Horizon's
   * own buy-buttons block does at render time (see CLAUDE.md), but done in
   * JS here since switching a radio doesn't trigger a section re-render.
   * Not a `ref`: the button sits inside <product-form-component>, which
   * would claim it as its own nearest-ancestor-component ref instead of ours.
   *
   * @param {boolean} isAvailable
   */
  #updateAddToCartAvailability(isAvailable) {
    const button = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.AkrPreorderProduct-checkout')
    );
    if (!button) return;

    button.disabled = !isAvailable;
    const label = button.querySelector('span');
    if (label) {
      label.textContent = isAvailable ? button.dataset.label : button.dataset.soldOutLabel;
    }
  }

  /**
   * Native label-click activation focuses the (visually hidden) radio and
   * scroll-into-views it, jumping the page. Handle the check + price update
   * ourselves and prevent that default activation.
   */
  selectVariant(event) {
    const input = event.target.querySelector('input[type="radio"]');
    if (!input || input.checked) return;

    event.preventDefault();
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));

    const { image, imageSrcset } = input.dataset;
    if (!image) return;

    this.#setActiveThumb((thumb) => thumb.dataset.src === image);
    this.#swapImage(image, imageSrcset);
  }

  /** @param {(thumb: HTMLButtonElement) => boolean} matches */
  #setActiveThumb(matches) {
    for (const thumb of this.refs.thumbs || []) {
      thumb.classList.toggle('is-active', matches(thumb));
    }
  }

  /** @param {PointerEvent} event */
  #onSwipeStart = (event) => {
    if (event.pointerType !== 'touch') return;

    this.#swipeStartX = event.clientX;
    this.#swipeStartY = event.clientY;
    this.refs.gallery.addEventListener('pointerup', this.#onSwipeEnd);
    this.refs.gallery.addEventListener('pointercancel', this.#removeSwipeEndListeners);
  };

  /** @param {PointerEvent} event */
  #onSwipeEnd = (event) => {
    this.#removeSwipeEndListeners();

    const deltaX = event.clientX - this.#swipeStartX;
    const deltaY = event.clientY - this.#swipeStartY;
    // A mostly-vertical drag is the user scrolling the page, not swiping
    // the gallery — only treat it as a swipe once horizontal movement
    // dominates and clears the threshold.
    if (Math.abs(deltaX) < PreorderProduct.#swipeThreshold || Math.abs(deltaX) < Math.abs(deltaY))
      return;

    this.#goToAdjacentThumb(deltaX < 0 ? 1 : -1);
  };

  #removeSwipeEndListeners = () => {
    this.refs.gallery?.removeEventListener('pointerup', this.#onSwipeEnd);
    this.refs.gallery?.removeEventListener('pointercancel', this.#removeSwipeEndListeners);
  };

  /** @param {1 | -1} direction */
  #goToAdjacentThumb(direction) {
    const thumbs = this.refs.thumbs;
    if (!thumbs?.length) return;

    const currentIndex = thumbs.findIndex((thumb) => thumb.classList.contains('is-active'));
    const next = thumbs[(currentIndex + direction + thumbs.length) % thumbs.length];
    if (!next) return;

    this.#setActiveThumb((thumb) => thumb === next);
    this.#swapImage(next.dataset.src, next.dataset.srcset);
  }

  /**
   * Two stacked <img> layers (see akr-preorder-product.liquid) crossfade
   * instead of fading a single <img> to transparent and back — swapping
   * `src` on one element always has a beat where it shows nothing/the old
   * frame underneath, which read as a flash to white.
   *
   * Rapid clicks can start a new crossfade before a previous one finishes —
   * targeting by index (not by "the element mid-transition") and killing
   * any tweens on both layers before starting new ones means a stale call
   * just gets redirected/interrupted instead of racing a previous one.
   *
   * @param {string | undefined} src
   * @param {string | undefined} [srcset]
   */
  #swapImage(src, srcset) {
    const images = this.refs.images;
    const current = images?.[this.#activeImageIndex];
    const next = images?.[1 - this.#activeImageIndex];
    if (!src || !current || !next || current.src === src) return;

    next.srcset = srcset ?? '';
    next.src = src;
    this.#activeImageIndex = 1 - this.#activeImageIndex;

    // decode() starts as soon as the browser has the bytes — runs in
    // parallel with the (possibly not-yet-loaded) GSAP script rather than
    // waiting on it first, so we're not adding load latency to the fade.
    const nextFramePainted = (next.decode?.() ?? Promise.resolve()).catch(() => {});

    Promise.all([ensureGsap(), nextFramePainted]).then(() => {
      gsap.killTweensOf([current, next]);
      gsap.to(current, { opacity: 0, duration: 0.3, ease: 'power1.inOut' });
      gsap.to(next, {
        opacity: 1,
        duration: 0.3,
        ease: 'power1.inOut',
        onComplete: () => {
          current.classList.remove('is-active');
          current.setAttribute('aria-hidden', 'true');
          next.classList.add('is-active');
          next.removeAttribute('aria-hidden');
        },
      });
    });
  }
}

customElements.define('akr-preorder-product', PreorderProduct);
